<template>
  <section class="page-banner" :style="bannerVars">
    <div
      class="core-container page-banner__inner"
      :class="{ 'page-banner__inner--safe': safeBottom }"
    >
      <div class="page-banner__media">
        <img :src="src" :alt="alt" class="page-banner__img" />
      </div>
      <div v-if="safeBottom" class="page-banner__safe" aria-hidden="true" />
      <div
        v-if="$slots.default"
        :class="[
          'page-banner__content',
          { 'is-align-start': contentAlign === 'start' },
          { 'is-align-end': contentAlign === 'end' }
        ]"
      >
        <slot />
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'PageBanner',
  props: {
    src: {
      type: String,
      required: true
    },
    alt: {
      type: String,
      default: ''
    },
    fillLeft: {
      type: String,
      default: ''
    },
    fillRight: {
      type: String,
      default: ''
    },
    minHeight: {
      type: String,
      default: 'var(--banner-min-height)'
    },
    autoHeight: {
      type: Boolean,
      default: false
    },
    contentAlign: {
      type: String,
      default: 'center',
      validator: value => ['center', 'start', 'end'].includes(value)
    },
    safeBottom: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    bannerVars() {
      const vars = {}
      if (this.fillLeft) vars['--banner-fill-left'] = this.fillLeft
      if (this.fillRight) vars['--banner-fill-right'] = this.fillRight
      if (this.autoHeight) {
        vars['--banner-block-min-height'] = 'auto'
      } else if (this.minHeight) {
        vars['--banner-block-min-height'] = this.minHeight
      }
      return vars
    }
  }
}
</script>

<style scoped>
.page-banner {
  --banner-fill-left: var(--color-banner-fill-left);
  --banner-fill-right: var(--color-banner-fill-right);
  --banner-block-min-height: var(--banner-min-height);
  background: linear-gradient(
    90deg,
    var(--banner-fill-left) 0%,
    var(--banner-fill-left) 42%,
    var(--banner-fill-right) 58%,
    var(--banner-fill-right) 100%
  );
}

.page-banner__inner {
  position: relative;
  min-height: var(--banner-block-min-height, auto);
}

.page-banner__media {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: var(--banner-block-min-height, auto);
}

.page-banner__safe {
  height: var(--banner-safe-bottom);
  background: linear-gradient(
    90deg,
    var(--banner-fill-left) 0%,
    var(--banner-fill-left) 42%,
    var(--banner-fill-right) 58%,
    var(--banner-fill-right) 100%
  );
}

.page-banner__inner--safe .page-banner__content {
  bottom: var(--banner-safe-bottom);
}

.page-banner__media::before,
.page-banner__media::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: var(--banner-edge-fade-width);
  z-index: 1;
  pointer-events: none;
}

.page-banner__media::before {
  left: 0;
  background: linear-gradient(
    to right,
    var(--banner-fill-left),
    transparent
  );
}

.page-banner__media::after {
  right: 0;
  background: linear-gradient(
    to left,
    var(--banner-fill-right),
    transparent
  );
}

.page-banner__img {
  width: 100%;
  height: auto;
  display: block;
  position: relative;
  z-index: 0;
}

.page-banner__content {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  pointer-events: none;
}

.page-banner__content.is-align-start {
  align-items: flex-start;
  padding-top: var(--banner-content-padding-top);
}

.page-banner__content.is-align-end {
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  padding-bottom: var(--banner-content-padding-bottom);
}

.page-banner__content > * {
  pointer-events: auto;
}
</style>
